import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OnboardingChecklist = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/onboarding-checklist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
        if (res.data.completed === res.data.total) setCollapsed(true);
      } catch {}
    };
    fetchChecklist();
  }, [token]);

  if (!data) return null;

  const pct = Math.round((data.completed / data.total) * 100);

  return (
    <Card className="p-5 border border-[#E2E8F0]" data-testid="onboarding-checklist">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
        data-testid="checklist-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.5" fill="none" stroke="#E2E8F0" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15.5" fill="none"
                stroke="#1d4ed8" strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${pct} ${100 - pct}`}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#1d4ed8]">
              {data.completed}/{data.total}
            </span>
          </div>
          <div>
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Getting Started
            </h3>
            <p className="text-xs text-[#475569]">
              {data.completed === data.total ? 'All done!' : `${data.total - data.completed} steps remaining`}
            </p>
          </div>
        </div>
        {collapsed ? <ChevronDown className="h-4 w-4 text-[#475569]" /> : <ChevronUp className="h-4 w-4 text-[#475569]" />}
      </div>

      {!collapsed && (
        <div className="mt-4 space-y-2" data-testid="checklist-steps">
          {data.steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                step.done
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'hover:bg-[#F1F5F9] text-[#334155]'
              }`}
              onClick={() => !step.done && navigate(step.link)}
              data-testid={`checklist-step-${step.id}`}
            >
              {step.done ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-[#94A3B8] shrink-0" />
              )}
              <span className={`text-sm ${step.done ? 'line-through opacity-70' : 'font-medium'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
